export type CalendarMonth = { readonly year: number; readonly month: number };

export type CalendarDay = { readonly date: string; readonly day: number; readonly inMonth: boolean };

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/u;
const DAYS_PER_WEEK = 7;
const WEEKS_SHOWN = 6;
const MONDAY_REFERENCE = { year: 2024, month: 0, day: 1 } as const;

const pad = (value: number, length: number): string => String(value).padStart(length, '0');

const utcOf = (year: number, month: number, day: number): Date => new Date(Date.UTC(year, month, day));

const isoOf = (date: Date): string =>
  `${pad(date.getUTCFullYear(), 4)}-${pad(date.getUTCMonth() + 1, 2)}-${pad(date.getUTCDate(), 2)}`;

export const isCalendarDate = (value: string): boolean => CALENDAR_DATE.test(value);

export const todayIn = (now: Date): string =>
  `${pad(now.getFullYear(), 4)}-${pad(now.getMonth() + 1, 2)}-${pad(now.getDate(), 2)}`;

export const monthOf = (date: string): CalendarMonth => {
  const [, year, month] = CALENDAR_DATE.exec(date) ?? [];
  return { year: Number(year), month: Number(month) - 1 };
};

export const shiftMonth = ({ year, month }: CalendarMonth, months: number): CalendarMonth => {
  const shifted = utcOf(year, month + months, 1);
  return { year: shifted.getUTCFullYear(), month: shifted.getUTCMonth() };
};

export const shiftDays = (date: string, days: number): string => {
  const { year, month } = monthOf(date);
  return isoOf(utcOf(year, month, Number(date.slice(8)) + days));
};

export const shiftDateByMonths = (date: string, months: number): string => {
  const target = shiftMonth(monthOf(date), months);
  const lastDay = utcOf(target.year, target.month + 1, 0).getUTCDate();
  return isoOf(utcOf(target.year, target.month, Math.min(Number(date.slice(8)), lastDay)));
};

export const monthGrid = ({ year, month }: CalendarMonth): readonly CalendarDay[] => {
  const first = utcOf(year, month, 1);
  const lead = (first.getUTCDay() + DAYS_PER_WEEK - 1) % DAYS_PER_WEEK;
  return Array.from({ length: DAYS_PER_WEEK * WEEKS_SHOWN }, (_, index) => {
    const date = utcOf(year, month, index - lead + 1);
    return { date: isoOf(date), day: date.getUTCDate(), inMonth: date.getUTCMonth() === month };
  });
};

export const weekdayNames = (locale: string): readonly string[] => {
  const format = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  return Array.from({ length: DAYS_PER_WEEK }, (_, index) =>
    format.format(utcOf(MONDAY_REFERENCE.year, MONDAY_REFERENCE.month, MONDAY_REFERENCE.day + index)),
  );
};

export const monthTitle = ({ year, month }: CalendarMonth, locale: string): string =>
  new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    utcOf(year, month, 1),
  );

export const isWithin = (date: string, min: string | undefined, max: string | undefined): boolean =>
  (min === undefined || min === '' || date >= min) && (max === undefined || max === '' || date <= max);
